import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { customerService } from '../services/customerService';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { Customer, CallLog, CallStatistics } from '../types';
import { FiPhone, FiCalendar, FiClock, FiUser, FiMessageSquare, FiCheckCircle, FiXCircle, FiArrowLeft, FiMail, FiMapPin, FiDollarSign, FiTarget, FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import PriceUpdateHistory from '../components/price/PriceUpdateHistory';
import CustomerStatusHistory from '../components/customers/CustomerStatusHistory';
import CustomerOrderHistory from '../components/customers/CustomerOrderHistory';
import ClickToCallButton from '../components/calls/ClickToCallButton';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, getTodayISO, getNowTimeHHMM, datetimeLocalToMysql } from '../utils/dateUtils';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import AllCallLogButton from '../components/calls/AllCallLogButton';
import { getSettingColorClass } from '../utils/settingColors';
import { CustomerOriginBadge, locationConversionCaption } from '../utils/customerOrigin';

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [statistics, setStatistics] = useState<CallStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetYear, setTargetYear] = useState(() => new Date().getFullYear());
  const [targetMonth, setTargetMonth] = useState(() => new Date().getMonth() + 1);
  const [targetLocations, setTargetLocations] = useState<{ customer_location_id: number; project_state?: string; project_district?: string; project_location?: string; target_tons: number }[]>([]);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'customer',
    entity_id: parseInt(id || '0'),
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound'
  });

  useEffect(() => {
    if (id) {
      loadCustomer();
      loadCallLogs();
      loadStatistics();
      setCallFormData(prev => ({ ...prev, entity_id: parseInt(id) }));
    }
  }, [id]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const data = await customerService.getById(parseInt(id || '0'));
      setCustomer(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async () => {
    try {
      const data = await callLogService.getByEntity('customer', parseInt(id || '0'));
      setCallLogs(data);
    } catch (err) {
      console.error('Failed to load call logs', err);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await callLogService.getStatistics('customer', parseInt(id || '0'));
      setStatistics(data);
    } catch (err) {
      console.error('Failed to load statistics', err);
    }
  };

  const handleCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await callLogService.create(callFormData);
      setShowCallModal(false);
      setCallFormData({
        entity_type: 'customer',
        entity_id: parseInt(id || '0'),
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound'
      });
      loadCustomer();
      loadCallLogs();
      loadStatistics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log call');
    }
  };

  const canSetTargets = user?.role === 'org_admin' || user?.role === 'manager';
  const customerId = parseInt(id || '0');

  useEffect(() => {
    if (!showTargetModal || !customerId) return;
    let cancelled = false;
    setTargetLoading(true);
    customerService.getTargets(customerId, targetYear, targetMonth)
      .then((data) => {
        if (!cancelled) setTargetLocations(data.locations || []);
      })
      .catch(() => {
        if (!cancelled) setTargetLocations([]);
      })
      .finally(() => {
        if (!cancelled) setTargetLoading(false);
      });
    return () => { cancelled = true; };
  }, [showTargetModal, customerId, targetYear, targetMonth]);

  const monthOptions: { year: number; month: number; label: string }[] = (() => {
    const opts: { year: number; month: number; label: string }[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    for (let y = currentYear; y <= currentYear + 1; y++) {
      const startM = y === currentYear ? currentMonth : 1;
      for (let m = startM; m <= 12; m++) {
        opts.push({ year: y, month: m, label: `${monthNames[m - 1]} ${y}` });
      }
    }
    return opts;
  })();

  const handleSaveTargets = async () => {
    if (!customerId) return;
    setTargetSaving(true);
    try {
      await customerService.setTargets(customerId, {
        year: targetYear,
        month: targetMonth,
        targets: targetLocations.map((loc) => ({ customer_location_id: loc.customer_location_id, target_tons: Number(loc.target_tons) || 0 })),
      });
      setShowTargetModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save targets');
    } finally {
      setTargetSaving(false);
    }
  };


  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested': return 'bg-green-100 text-green-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'callback_requested': return 'bg-blue-100 text-blue-800';
      case 'no_response': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">Loading...</div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="text-center py-8 text-red-600">Customer not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
          >
            <Icon component={FiArrowLeft} className="w-4 h-4" />
            <span className="text-sm">Back to Customers</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Customer Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{customer.party_name}</h1>
              <div className="mb-2">
                <CustomerOriginBadge customer={customer} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                {customer.customer_code && (
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">Code:</span>
                    <span>{customer.customer_code}</span>
                  </div>
                )}
                {customer.contact_no && (
                  <div className="flex items-center space-x-1 gap-2">
                    <div className="flex items-center space-x-1">
                      <Icon component={FiPhone} className="w-4 h-4" />
                      <span>{customer.contact_no}</span>
                    </div>
                    <ClickToCallButton
                      entityType="customer"
                      entityId={customer.id}
                      entityName={customer.party_name}
                      phoneNumber={customer.contact_no}
                    />
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center space-x-1">
                    <Icon component={FiMail} className="w-4 h-4" />
                    <span>{customer.email}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 sm:mt-0">
              <span className={`px-2 py-1 rounded text-xs font-semibold border ${
                customer.status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                customer.status === 'dormant' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                customer.status === 'lost' ? 'bg-red-100 text-red-800 border-red-300' :
                'bg-gray-100 text-gray-800 border-gray-300'
              }`}>
                {customer.status}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm">
            {customer.contact_person && (
              <div>
                <span className="text-gray-500">Contact Person:</span>
                <p className="text-gray-900">{customer.contact_person}</p>
              </div>
            )}
            {customer.contact_no && (
              <div>
                <span className="text-gray-500">Contact No:</span>
                <p className="text-gray-900">{customer.contact_no}</p>
              </div>
            )}
            {customer.secondary_contact_person && (
              <div>
                <span className="text-gray-500">Secondary Contact Person:</span>
                <p className="text-gray-900">{customer.secondary_contact_person}</p>
              </div>
            )}
            {customer.secondary_contact_no && (
              <div>
                <span className="text-gray-500">Secondary Contact No:</span>
                <p className="text-gray-900">{customer.secondary_contact_no}</p>
              </div>
            )}
            {customer.office_address && (
              <div>
                <span className="text-gray-500">Address:</span>
                <p className="text-gray-900">{customer.office_address}</p>
              </div>
            )}
            {customer.sales_officer_name && (
              <div>
                <span className="text-gray-500">Sales Officer:</span>
                <p className="text-gray-900">{customer.sales_officer_name}</p>
              </div>
            )}
            {customer.brand_name && (
              <div>
                <span className="text-gray-500">Brand:</span>
                <p className="text-gray-900">{customer.brand_name}</p>
              </div>
            )}
            {customer.doj && (
              <div>
                <span className="text-gray-500">Date of Joining:</span>
                <p className="text-gray-900">{formatDate(customer.doj)}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500">Last Order Date:</span>
              <p className="text-gray-900">
                {customer.last_bill_date ? formatDate(customer.last_bill_date) : '-'}
              </p>
            </div>
            {customer.work_order_value && (
              <div>
                <span className="text-gray-500">Work Order Value:</span>
                <p className="text-gray-900">{formatCurrency(customer.work_order_value)}</p>
              </div>
            )}
            {customer.locations && customer.locations.length > 0 && (
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <span className="text-gray-500">Locations:</span>
                <div className="mt-1 space-y-2">
                  {customer.locations.map((loc, idx) => (
                    <div key={loc.id ?? idx} className="p-2 bg-gray-50 rounded text-sm">
                      <div className="font-medium text-gray-700">
                        {[loc.project_state, loc.project_district, loc.project_location].filter(Boolean).join(' - ') || 'Location ' + (idx + 1)}
                        <span className="ml-2 font-normal text-gray-500">({locationConversionCaption(loc)})</span>
                      </div>
                      {(loc.company_sales_person_display || loc.company_sales_person) && <div className="text-gray-600">Company Sales Officer: {loc.company_sales_person_display ?? loc.company_sales_person}</div>}
                      {loc.converted_by_name && (
                        <div className="text-gray-600">Converted by: {loc.converted_by_name}</div>
                      )}
                      {(loc.converted_from_tender_id || loc.tender_awarder) && (
                        <div className="text-gray-600">
                          Tender:{' '}
                          {loc.converted_from_tender_id ? (
                            <Link to={`/tenders/${loc.converted_from_tender_id}`} className="text-yellow-700 hover:underline">
                              #{loc.converted_from_tender_id}
                            </Link>
                          ) : null}
                          {loc.tender_awarder ? ` ${loc.tender_awarder}` : ''}
                          {loc.tender_date ? ` · ${formatDate(loc.tender_date)}` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {customer.tender_conversions && customer.tender_conversions.length > 0 && (
              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <span className="text-gray-500">Converted from tenders:</span>
                <ul className="mt-1 space-y-1 text-sm text-gray-900">
                  {customer.tender_conversions.map((conv) => (
                    <li key={conv.id ?? conv.tender_id}>
                      <Link to={`/tenders/${conv.tender_id}`} className="text-yellow-700 hover:underline">
                        Tender #{conv.tender_id}
                      </Link>
                      {conv.tender_awarder ? ` · ${conv.tender_awarder}` : ''}
                      {conv.tender_date ? ` · ${formatDate(conv.tender_date)}` : ''}
                      {conv.brand_name ? ` · ${conv.brand_name}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {customer.product_req && (
              <div>
                <span className="text-gray-500">Product Req:</span>
                <p className="text-gray-900">{customer.product_req}</p>
              </div>
            )}
            {customer.qty_req && (
              <div>
                <span className="text-gray-500">Qty Req:</span>
                <p className="text-gray-900">{customer.qty_req}</p>
              </div>
            )}
          </div>
        </div>

        {/* Call Statistics */}
        {statistics && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Statistics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{statistics.total_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Total Calls</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{statistics.answered_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Answered</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{statistics.no_answer_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">No Answer</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{statistics.interested_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Interested</div>
              </div>
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div className="flex justify-end gap-2 flex-wrap">
          {canSetTargets && (
            <button
              onClick={() => {
                const now = new Date();
                setTargetYear(now.getFullYear());
                setTargetMonth(now.getMonth() + 1);
                setShowTargetModal(true);
              }}
              className="flex items-center space-x-2 bg-amber-500 text-gray-900 px-4 py-2 rounded-md hover:bg-amber-600 text-sm font-medium font-bold"
            >
              <Icon component={FiTarget} className="w-4 h-4" />
              <span>Target</span>
            </button>
          )}
          <button
            onClick={() => setShowCallModal(true)}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
          >
            <Icon component={FiPhone} className="w-4 h-4" />
            <span>Log New Call</span>
          </button>
        </div>

        {/* Target modal */}
        {showTargetModal && customer && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Monthly target (tons) – {customer.party_name}
                </h3>
                <button onClick={() => setShowTargetModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  value={`${targetYear}-${targetMonth}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-').map(Number);
                    setTargetYear(y);
                    setTargetMonth(m);
                  }}
                  className="block w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                >
                  {monthOptions.map((opt) => (
                    <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {targetLoading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : targetLocations.length === 0 ? (
                <p className="text-sm text-gray-500">No locations. Add locations in the customer form first.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {targetLocations.map((loc) => (
                    <div key={loc.customer_location_id} className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[120px] text-sm text-gray-700">
                        {[loc.project_state, loc.project_district, loc.project_location].filter(Boolean).join(', ') || `Location #${loc.customer_location_id}`}
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={loc.target_tons ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            setTargetLocations((prev) =>
                              prev.map((l) =>
                                l.customer_location_id === loc.customer_location_id ? { ...l, target_tons: isNaN(v) ? 0 : v } : l
                              )
                            );
                          }}
                          className="block w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder="Tons"
                        />
                      </div>
                      <span className="text-xs text-gray-500">tons</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowTargetModal(false)} className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
                <button type="button" onClick={handleSaveTargets} disabled={targetSaving || targetLocations.length === 0} className="px-3 py-1.5 bg-amber-500 text-gray-900 rounded hover:bg-amber-600 text-sm font-medium disabled:opacity-50">{targetSaving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Call History Timeline */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {callLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(log.call_date, log.call_time)}
                      </span>
                      {log.called_by_name && (
                        <span className="text-xs text-gray-500">by {log.called_by_name}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                      {log.call_status && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${getSettingColorClass(log.call_status_color_code)}`}>
                          {log.call_status}
                        </span>
                      )}
                      {log.call_outcome && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}>
                          {log.call_outcome}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.notes && (
                    <div className="text-sm text-gray-700 mt-2 flex items-start space-x-2">
                      <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="flex-1">{log.notes}</p>
                    </div>
                  )}
                  {(log.next_call_date || log.next_call_notes) && (
                    <div className="mt-2 text-xs text-indigo-600">
                      {log.next_call_date && (
                        <div className="flex items-center space-x-1">
                          <Icon component={FiCalendar} className="w-3 h-3" />
                          <span>Next call: {formatDateTime(log.next_call_date)}</span>
                        </div>
                      )}
                      {log.next_call_notes && (
                        <p className="mt-0.5 pl-4 text-indigo-800">Next call notes: {log.next_call_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price Update History */}
        {customer && (
          <PriceUpdateHistory
            entityType="customer"
            entityId={customer.id}
            entityName={customer.party_name}
          />
        )}

        {/* Order History */}
        {customer && (
          <div className="mt-6">
            <CustomerOrderHistory customerId={customer.id} onOrderSaved={loadCustomer} />
          </div>
        )}

        {/* Status Change History */}
        {customer && (
          <div className="mt-6">
            <CustomerStatusHistory customerId={customer.id} />
          </div>
        )}

        {/* Call Log Modal */}
        {showCallModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Log New Call</h3>
                <button
                  type="button"
                  onClick={() => setShowCallModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              {customer && <PriorCallAlert entityType="customer" entityId={customer.id} />}
              {customer && (
                <div className="mb-3">
                  <AllCallLogButton
                    entityType="customer"
                    entityId={customer.id}
                    entityName={customer.party_name}
                  />
                </div>
              )}
              <form onSubmit={handleCallSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                    <input
                      type="date"
                      required
                      value={callFormData.call_date}
                      onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                    <input
                      type="time"
                      value={callFormData.call_time}
                      onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status *</label>
                  <CallStatusSelect
                    required
                    value={callFormData.call_status_id}
                    onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome *</label>
                  <select
                    required
                    value={callFormData.call_outcome || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                    disabled={callOutcomeOptionsLoading}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                  >
                    <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                    {callOutcomeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={callFormData.notes || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={4}
                    placeholder="Enter call notes..."
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                  <input
                    type="datetime-local"
                    value={callFormData.next_call_date ? (() => {
                      // Convert ISO string (UTC) to local datetime-local format (YYYY-MM-DDTHH:mm)
                      const date = new Date(callFormData.next_call_date);
                      // Get local time components
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })() : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setCallFormData({ ...callFormData, next_call_date: datetimeLocalToMysql(e.target.value) });
                      } else {
                        setCallFormData({ ...callFormData, next_call_date: undefined });
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
                {callFormData.next_call_date && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
                    <textarea
                      value={callFormData.next_call_notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, next_call_notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={2}
                      placeholder="Notes for next call..."
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Log Call
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CustomerDetail;

